import { Label } from '@radix-ui/react-label'
import { ReactNode, Ref } from 'react'

import { SizeOptions } from '../../../types/types'
import { AstraParagraph } from '../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { AstraErrorText } from './AstraErrorText'
import { AstraHelperText } from './AstraHelperText'

export type InputBase = {
    startIcon?: ReactNode
    endIcon?: ReactNode
    helperText?: string
    errorText?: string
    error?: boolean
    label?: string
    fullWidth?: boolean
    borderless?: boolean
}

export type AstraInputProps = {
    id?: string
    disabled?: boolean
    readOnly?: boolean
    size?: SizeOptions
    className?: string
    children?: ReactNode
    labelHtmlFor?: string
    onClick?: () => void
    ref?: Ref<HTMLDivElement>
} & InputBase

export const getHelperTextId = (id: string) => `helper-text-${id}`

export const getErrorTextId = (id: string) => `error-text-${id}`

/**
 * The `aria-describedby` value for an input inside an `AstraInputWrapper` with the same `id`. Screen readers read the
 * helper text and, while the input is in error, the error text when the input gets focus.
 */
export const getInputDescribedBy = ({ id, helperText, errorText, error }: { id: string } & Pick<InputBase, 'helperText' | 'errorText' | 'error'>) => {
    const ids = [helperText && getHelperTextId(id), error && errorText && getErrorTextId(id)].filter(Boolean)
    return ids.length > 0 ? ids.join(' ') : undefined
}

/**
 * Cursor for a frame where a click anywhere focuses a text input, so the whole frame reads as typeable. Buttons placed in
 * the frame, like a clear or send button, keep a regular cursor. Disabled frames can't be typed in, so they keep the
 * regular cursor too.
 */
export const textInputFrameCursorStyles = (disabled?: boolean) => (disabled ? '' : 'cursor-text [&_button]:cursor-default')

export const inputTypographyStyles: Record<SizeOptions, string> = {
    small: 'text-body-small leading-body-small tracking-body-small',
    medium: 'text-body leading-body tracking-body',
    large: 'text-body leading-body tracking-body',
}

export const inputSizeStyles: Record<SizeOptions, string> = {
    small: 'min-h-5', // 20px, 1.25rem
    medium: 'min-h-7', // 28px, 1.75rem
    large: 'min-h-9', // 36px, 2.25rem
}

const inputBaseStyles = 'overflow-hidden flex items-center gap-2 justify-between bg-surface-base px-2 rounded-md text-default group'

// Only hovering eases the border in. Focus, errors and leaving hover change instantly, so keyboard focus never lags behind
// the key press and the border never drifts apart from the outline, which isn't transitioned.
const borderTransitionStyles = 'hover:transition-[border-color] hover:duration-150 hover:ease-out focus-within:!transition-none'

// The focus ring is forced over any hover border: Tailwind emits hover variants after focus-within,
// so at equal specificity a hovered input would otherwise keep its hover border while focused.
const borderStyles = `border border-base hover:border-hover outline-none focus-within:!border-ring outline-2 outline-offset-0 focus-within:!outline focus-within:!outline-brand-subtle ${borderTransitionStyles}`

const disabledStyles = 'text-disabled bg-surface-disabled border-disabled hover:!border-disabled'

// Read-only inputs can be focused to select text, but they can't be edited, so focus doesn't light up the border.
const readOnlyStyles = 'text-muted bg-surface-muted border-base focus-within:!border-base hover:!border-base focus-within:!outline-0'

// The error outline sits flush against the border so the two read as one thick, two-tone edge. Focus keeps the
// critical border so the error stays visible while the user corrects it.
const errorStyles =
    'border-critical-strong outline outline-2 outline-offset-0 outline-critical-subtle hover:border-critical-base focus-within:!border-critical-strong focus-within:!outline-critical-subtle'

export const AstraInputWrapper = ({
    id,
    label,
    helperText,
    errorText,
    error,
    disabled,
    readOnly,
    fullWidth,
    className,
    children,
    labelHtmlFor,
    onClick,
    ref,
    startIcon,
    endIcon,
    size = 'small',
    borderless = false,
}: AstraInputProps) => (
    <div className={astraTwMerge('relative flex flex-col gap-1', fullWidth ? 'w-full' : '')} ref={ref}>
        {label && (
            <Label htmlFor={labelHtmlFor}>
                <AstraParagraph as="span" variant="body-small" weight="medium" className="block select-none">
                    {label}
                </AstraParagraph>
            </Label>
        )}
        <AstraHelperText helperText={helperText} id={id} />
        <div
            className={astraTwMerge(
                inputBaseStyles,
                inputSizeStyles[size],
                borderless ? '' : borderStyles,
                error ? errorStyles : '',
                disabled ? disabledStyles : '',
                readOnly ? readOnlyStyles : '',
                className
            )}
            aria-label={label}
            onClick={onClick}
        >
            {startIcon && <span>{startIcon}</span>}
            {children}
            {endIcon && <span>{endIcon}</span>}
        </div>
        <AstraErrorText errorText={errorText} error={error} id={id} />
    </div>
)
